import { FC, SyntheticEvent, useCallback, useMemo } from 'react';
import { Icon, IconName } from '../Icon';

type Variant = 'default' | 'gray';

export type LinkProps = {
  /**
   * Label of the link
   * We only want to use this type of link for text, so we are not using the children prop
   */
  label?: string;
  /**
   * Target of the link
   */
  href?: string;
  /**
   * Variant to define the link style
   */
  variant?: Variant;
  /**
   * The icon to display before the link
   */
  icon?: IconName;
  /**
   * Should the icon be displayed left or right of the label
   */
  iconPosition?: 'left' | 'right';
  /**
   * Click action of the link
   */
  onClick?(event: SyntheticEvent): void;
  /**
   * Flag to disable link
   */
  disabled?: boolean;
};

const BASE_LINK_CLASSES = 'font-sans font-medium';
const DEFAULT_LINK_CLASSES = `${BASE_LINK_CLASSES} text-blue-200 hover:text-blue-100 hover:underline cursor-pointer`;
const gray_LINK_CLASSES = `${BASE_LINK_CLASSES} text-gray-600 hover:text-blue-100 hover:underline cursor-pointer`;
const DISABLED_LINK_CLASSES = `${BASE_LINK_CLASSES} text-gray-400 hover:text-gray-400 cursor-default`;

export const Link: FC<LinkProps> = ({
  label,
  href,
  variant,
  icon,
  // Icons should be placed on the left by default
  iconPosition = 'left',
  onClick,
  disabled,
}: LinkProps) => {
  const className = useMemo(() => {
    if (disabled) {
      return DISABLED_LINK_CLASSES;
    }

    switch (variant) {
      case 'gray':
        return gray_LINK_CLASSES;
      case 'default':
      default:
        return DEFAULT_LINK_CLASSES;
    }
  }, [disabled, variant]);

  const onClickHandler = useCallback(
    (event: SyntheticEvent) => {
      if (disabled) {
        event?.preventDefault();
        return false;
      }

      if (onClick) {
        onClick(event);
      } else {
        return true;
      }
    },
    [disabled, onClick],
  );

  const iconElement = useMemo(
    () =>
      icon && (
        <span
          data-testid="icon"
          className={`m${
            // Whichever side the icon is positioned, put margin on the opposite side to
            // push it away from the label. We don't need to check if iconPosition exists
            // because it's 'left' by default.
            iconPosition === 'left' ? 'r' : 'l'
          }-2 inline-block w-6 flex-shrink-0`}
        >
          <Icon name={icon} inline={true} />
        </span>
      ),
    [icon, iconPosition],
  );

  return (
    <a href={href} className={className} onClick={onClickHandler} data-testid='link'>
      {iconPosition === 'left' && iconElement}
      <span>{label}</span>
      {iconPosition === 'right' && iconElement}
    </a>
  );
};
